import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMachines, sendAdminPush } from '../../API/adminAPI';

const Icon = ({ d, size = 15, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);
const EditIcon    = 'M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7 M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z';
const PlusIcon    = 'M12 5v14 M5 12h14';
const SearchIcon  = 'M21 21l-4.35-4.35M17 11A6 6 0 105 11a6 6 0 0012 0z';
const RefreshIcon = 'M1 4v6h6 M23 20v-6h-6 M20.49 9A9 9 0 005.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 013.51 15';
const WarnIcon    = 'M12 9v4 M12 17h.01 M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z';
const BellIcon    = 'M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9 M13.73 21a2 2 0 01-3.46 0';
const CloseIcon   = 'M18 6L6 18 M6 6l12 12';

// ── Push modal (same as MachinesTab) ─────────────────────────────────────────
function PushModal({ machine, onClose }) {
  const [title,   setTitle]   = useState(`Update from ${machine.machine_name || machine.machine_id}`);
  const [body,    setBody]    = useState('');
  const [sending, setSending] = useState(false);
  const [result,  setResult]  = useState(null);
  const [error,   setError]   = useState('');

  const send = async (target) => {
    if (!title.trim() || !body.trim()) { setError('Title and message are required.'); return; }
    setSending(true); setError(''); setResult(null);
    try {
      const res = await sendAdminPush({ machine_id: machine.machine_id, title: title.trim(), body: body.trim(), target });
      setResult(res);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to send push notification.');
    } finally { setSending(false); }
  };

  const inp = { width: '100%', padding: '9px 12px', fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--ink-900)', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--r-md)', outline: 'none', boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(12,14,22,.4)', backdropFilter: 'blur(3px)' }} onClick={onClose} />
      <div style={{ position: 'relative', background: 'var(--bg-surface)', borderRadius: 'var(--r-xl)', boxShadow: 'var(--sh-lg)', width: '100%', maxWidth: 480, padding: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 15, color: 'var(--ink-900)' }}>Send Push Notification</div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-500)', marginTop: 3 }}>{machine.machine_id}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-500)', padding: 4 }}><Icon d={CloseIcon} size={18} /></button>
        </div>
        {result ? (
          <div>
            <div style={{ background: 'var(--online-bg)', border: '1px solid var(--online-border)', borderRadius: 'var(--r-md)', padding: '14px 16px', marginBottom: 16 }}>
              <div style={{ fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 13, color: 'var(--online)', marginBottom: 4 }}>✅ Notification sent</div>
              <div style={{ fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--ink-700)' }}>
                {result.target === 'self' ? `Sent to your ${result.devices} device${result.devices !== 1 ? 's' : ''}.` : `Sent to ${result.users} user${result.users !== 1 ? 's' : ''} of ${result.machine_id}.`}
              </div>
            </div>
            <button onClick={onClose} style={{ width: '100%', padding: '10px', background: 'var(--accent)', border: 'none', borderRadius: 'var(--r-md)', fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 500, color: '#fff', cursor: 'pointer' }}>Done</button>
          </div>
        ) : (
          <>
            {error && <div style={{ background: 'var(--offline-bg)', color: 'var(--offline)', borderRadius: 'var(--r-md)', padding: '10px 14px', fontSize: 13, marginBottom: 14 }}>{error}</div>}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontFamily: 'var(--font-sans)', fontSize: 12, fontWeight: 500, color: 'var(--ink-700)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.4 }}>Title</label>
              <input value={title} onChange={e => setTitle(e.target.value)} style={inp} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontFamily: 'var(--font-sans)', fontSize: 12, fontWeight: 500, color: 'var(--ink-700)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.4 }}>Message</label>
              <textarea value={body} onChange={e => setBody(e.target.value)} rows={3} placeholder="Enter message for users of this machine…" style={{ ...inp, resize: 'vertical', minHeight: 80 }} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => send('self')} disabled={sending} style={{ flex: 1, padding: '10px', background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--r-md)', fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 500, color: 'var(--ink-700)', cursor: 'pointer' }}>Test (send to myself)</button>
              <button onClick={() => send('machine')} disabled={sending} style={{ flex: 1, padding: '10px', background: sending ? 'var(--border-strong)' : 'var(--accent)', border: 'none', borderRadius: 'var(--r-md)', fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 500, color: '#fff', cursor: sending ? 'not-allowed' : 'pointer' }}>
                {sending ? 'Sending…' : 'Send to all users'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const SuperMachinesTab = () => {
  const navigate  = useNavigate();
  const [machines,  setMachines]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [search,    setSearch]    = useState('');
  const [pushModal, setPushModal] = useState(null);

  const load = async () => {
    setLoading(true); setError('');
    try { const d = await getMachines(); setMachines(d.machines || []); }
    catch (e) { setError(e?.response?.data?.error || 'Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = machines.filter(m =>
    m.machine_id?.toLowerCase().includes(search.toLowerCase()) ||
    m.machine_name?.toLowerCase().includes(search.toLowerCase()) ||
    (m.owned_by || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      {error && <div style={{ background: 'var(--offline-bg)', color: 'var(--offline)', borderRadius: 'var(--r-md)', padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>{error}</div>}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 380 }}>
          <div style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink-300)', pointerEvents: 'none' }}>
            <Icon d={SearchIcon} />
          </div>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by ID, name or owner…"
            style={{ width: '100%', paddingLeft: 34, paddingRight: 12, paddingTop: 8, paddingBottom: 8, fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--ink-700)', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--r-md)', outline: 'none', boxSizing: 'border-box' }} />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={load} style={{ display: 'flex', alignItems: 'center', padding: '7px 10px', background: 'none', border: '1px solid var(--border-subtle)', borderRadius: 'var(--r-md)', cursor: 'pointer', color: 'var(--ink-500)' }}>
            <Icon d={RefreshIcon} size={14} />
          </button>
          <button onClick={() => navigate('/app/machines/new')} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'var(--accent)', border: 'none', borderRadius: 'var(--r-md)', cursor: 'pointer', color: '#fff', fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 500 }}>
            <Icon d={PlusIcon} size={14} color="#fff" />
            Commission Machine
          </button>
        </div>
      </div>
      <div style={{ fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--ink-500)', marginBottom: 10 }}>{filtered.length} machine{filtered.length !== 1 ? 's' : ''}</div>

      {loading ? (
        <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--ink-300)', fontFamily: 'var(--font-sans)', fontSize: 13 }}>Loading…</div>
      ) : (
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-hair)', borderRadius: 'var(--r-lg)', overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 100px 100px', padding: '10px 16px', borderBottom: '2px solid var(--border-strong)' }}>
            {['Machine ID', 'Name', 'OEM Owner', 'Timezone', ''].map((h, i) => (
              <div key={i} style={{ fontFamily: 'var(--font-sans)', fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--ink-500)' }}>{h}</div>
            ))}
          </div>
          {filtered.length === 0 ? (
            <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--ink-300)', fontFamily: 'var(--font-sans)', fontSize: 13 }}>No machines found</div>
          ) : filtered.map(m => (
            <div key={m.id}
              onClick={() => navigate(`/app/machines/${m.id}/edit`, { state: { machine: m } })}
              style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 100px 100px', padding: '13px 16px', borderBottom: '1px solid var(--border-hair)', cursor: 'pointer', alignItems: 'center', transition: 'background var(--t-fast)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-raised)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--accent)', background: 'var(--accent-soft)', display: 'inline-block', padding: '2px 8px', borderRadius: 4, border: '1px solid rgba(8,151,190,0.2)' }}>{m.machine_id}</div>
              <div style={{ fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 500, color: 'var(--ink-900)' }}>{m.machine_name || '—'}</div>
              <div>
                {m.owned_by ? (
                  <span style={{ fontFamily: 'var(--font-sans)', fontSize: 12, background: 'var(--accent-soft)', color: 'var(--accent)', padding: '2px 8px', borderRadius: 4, border: '1px solid rgba(8,151,190,0.2)' }}>{m.owned_by}</span>
                ) : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--warn)' }}>
                    <Icon d={WarnIcon} size={12} color="var(--warn)" /> Unassigned
                  </span>
                )}
              </div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-500)' }}>
                UTC{m.timezone_offset_minutes >= 0 ? '+' : ''}{(m.timezone_offset_minutes / 60).toFixed(1).replace('.0', '')}h
              </div>
              <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
                <button onClick={() => navigate(`/app/machines/${m.id}/edit`, { state: { machine: m } })}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-500)', padding: 4, borderRadius: 'var(--r-sm)' }}
                  onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-500)'}>
                  <Icon d={EditIcon} />
                </button>
                <button title="Send push notification" onClick={() => setPushModal(m)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-500)', padding: 4, borderRadius: 'var(--r-sm)' }}
                  onMouseEnter={e => e.currentTarget.style.color = 'var(--ai)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-500)'}>
                  <Icon d={BellIcon} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {pushModal && <PushModal machine={pushModal} onClose={() => setPushModal(null)} />}
    </div>
  );
};

export default SuperMachinesTab;
